import { ChangeEvent, useMemo, useState } from "react";
import { t } from "@lingui/macro";
import { useSWRConfig } from "swr";
import { differenceInYears } from "date-fns";
import {
  FitnessProfileResponseType,
  FitnessProfileAlgorithm,
  ThreeDPProfileFromKnownTimesOnlyType,
} from "@WahooFitness/cloud-client-types";
import { FitnessProfiles, ThreeDPProfileFromKnownTimesOnly } from "@WahooFitness/cloud-client-ts";
import {
  useCloudContext,
  useConfigContext,
  useDrawerContext,
  useGuardedUpdater,
  useSnackbarContext,
  useUserContext,
} from "@WahooFitness/wahoo-offline-mfe";

interface FieldValidation {
  min: number;
  max: number;
}

const useManualRunningProfileUpdate = ({
  fitnessProfile,
  handleClose,
  fieldValidations,
}: {
  fitnessProfile: FitnessProfileResponseType | undefined;
  handleClose: () => void;
  fieldValidations: Record<string, FieldValidation>;
}) => {
  const [formIsTouched, setFormIsTouched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});

  // Cloud context and utilities
  const { getCloudClient } = useCloudContext();
  const { wahooToken } = useConfigContext();
  const { currentUser } = useUserContext();
  const { update } = useGuardedUpdater();
  const { enqueueSnackbar } = useSnackbarContext();
  const { setDrawer } = useDrawerContext();
  const { mutate } = useSWRConfig();
  const fitnessProfileClient = useMemo(() => getCloudClient(FitnessProfiles), [getCloudClient]);
  const threeDPProfileFromKnownTimesOnlyClient = useMemo(
    () => getCloudClient(ThreeDPProfileFromKnownTimesOnly),
    [getCloudClient]
  );

  const initialFormValues = useMemo<Record<string, number | null>>(
    () => ({
      dur_1: fitnessProfile?.dur_1 ?? null,
      dur_2: fitnessProfile?.dur_2 ?? null,
      dur_3: fitnessProfile?.dur_3 ?? null,
      dur_4: fitnessProfile?.dur_4 ?? null,
    }),
    [fitnessProfile?.dur_1, fitnessProfile?.dur_2, fitnessProfile?.dur_3, fitnessProfile?.dur_4]
  );

  const [formValues, setFormValues] = useState<Record<string, number | null>>(initialFormValues);

  const handleInputChange = (
    event:
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | { target: { name: string; value: number | null } }
  ) => {
    const { name } = event.target;
    const rawValue = event.target.value;
    const value = typeof rawValue === "string" ? null : rawValue;

    const nextValues = { ...formValues, [name]: value };
    setFormValues(nextValues);
    setFieldErrors((prev) => ({ ...prev, [name]: null }));
    setFormIsTouched(
      Object.keys(nextValues).some((key) => nextValues[key] !== initialFormValues[key])
    );
  };

  const validateField = (name: string, value: number | null): string | null => {
    const validation = fieldValidations[name];
    if (!validation || value === null) return null;
    if (value < validation.min) return t`Value is too low`;
    if (value > validation.max) return t`Value is too high`;
    return null;
  };

  const handleBlur = (name: string) => {
    const error = validateField(name, formValues[name]);
    setFieldErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleReset = () => {
    const cleared: Record<string, number | null> = {
      dur_1: null,
      dur_2: null,
      dur_3: null,
      dur_4: null,
    };
    setFormValues(cleared);
    setFormIsTouched(Object.keys(cleared).some((key) => cleared[key] !== initialFormValues[key]));
    setFieldErrors({});
  };

  const handleSave = async () => {
    if (!fitnessProfile) return;

    const errors: Record<string, string | null> = {};
    for (const [name, value] of Object.entries(formValues)) {
      errors[name] = validateField(name, value);
    }
    setFieldErrors(errors);
    if (Object.values(errors).some((e) => e !== null)) return;

    setIsSaving(true);

    const hasAnyDur = Object.values(formValues).some((v) => v !== null);

    try {
      let updatedProfile: typeof fitnessProfile;

      if (hasAnyDur) {
        const threeDPInput: ThreeDPProfileFromKnownTimesOnlyType = {
          option: "profile_from_known_times",
          athlete: {
            sex: currentUser?.gender === 0 ? "male" : "female",
            age: currentUser?.birth
              ? differenceInYears(new Date(), new Date(currentUser.birth + "T00:00:00"))
              : null,
            weight: currentUser?.weight != null ? Number(currentUser.weight) : null,
            height: currentUser?.height != null ? Number(currentUser.height) : null,
            five_k_time_seconds: formValues.dur_1,
            ten_k_time_seconds: formValues.dur_2,
            half_marathon_time_seconds: formValues.dur_3,
            marathon_time_seconds: formValues.dur_4,
            user_id: currentUser?.id,
          },
        };
        const threeDPResults = await threeDPProfileFromKnownTimesOnlyClient.post(
          wahooToken,
          threeDPInput
        );

        updatedProfile = {
          ...fitnessProfile,
          dim_1: threeDPResults.profile.threedp.FTP,
          dim_2: threeDPResults.profile.threedp.MAP,
          dim_3: threeDPResults.profile.threedp.AC,
          dim_4: null,
          dur_1: formValues.dur_1,
          dur_2: formValues.dur_2,
          dur_3: formValues.dur_3,
          dur_4: formValues.dur_4,
          fitness_profile_algorithm_id: FitnessProfileAlgorithm.Custom,
        };
      } else {
        updatedProfile = {
          ...fitnessProfile,
          dur_1: null,
          dur_2: null,
          dur_3: null,
          dur_4: null,
        };
      }

      await update(
        () =>
          fitnessProfileClient.updateFitnessProfile(
            fitnessProfile.id,
            updatedProfile,
            wahooToken,
            false
          ),
        {
          afterUpdate: () => {
            mutate(["userFitnessProfiles", wahooToken]);
            setFormIsTouched(false);
            setIsSaving(false);
            enqueueSnackbar({
              message: t`Your Running Athlete Profile values were successfully updated.`,
              severity: "success",
            });
            handleClose();
            setDrawer({ open: false });
          },
          onUpdateError: (error) => {
            console.log(error);
            setIsSaving(false);
            enqueueSnackbar({
              message: t`There was a problem saving your profile. Check your network and try again.`,
              severity: "error",
            });
          },
        }
      );
    } catch (error) {
      setIsSaving(false);
      enqueueSnackbar({
        message: t`There was a problem saving your profile. Check your network and try again.`,
        severity: "error",
      });
    }
  };

  const hasErrors = Object.values(fieldErrors).some((e) => e !== null);
  const isSaveButtonDisabled = !formIsTouched || isSaving || hasErrors;

  return {
    isSaving,
    formValues,
    fieldErrors,
    handleInputChange,
    handleBlur,
    handleReset,
    handleSave,
    isSaveButtonDisabled,
  };
};

export default useManualRunningProfileUpdate;
